import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import moment from "moment";

import PrepaidDeferredRevenueBalanceFilters from "./PrepaidDeferredRevenueBalanceFilters";
import ReportService from "../../../services/reportService";
import PrepaidDeferredRevenueBalanceList from "./PrepaidDeferredRevenueBalanceList";
import { downloadCSV } from "../utils";
import { PREPAID_DEFERRED_REVENUE_BALANCE_COLUMNS } from "./columns";

import { formatDate } from "../../../helpers/date";

import * as exportActions from "../../../actions/exportActions";

const PrepaidDeferredRevenueBalancePage = ({
  history,
  actions,
  exported,
  exporting
}) => {
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);
  const [sorted, setSorted] = useState([{ id: "contract", desc: true }]);
  const [exportingCSV, setExportingCSV] = useState(false);

  const [filters, setFilters] = useState({
    page: 0,
    pageSize: 100,
    end_date: formatDate(new Date(), "yyyy-MM-dd")
  });

  const [data, setData] = useState([]);

  useEffect(() => {
    if (exported) {
      const url = window.URL.createObjectURL(
        new Blob([exported], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `Prepaid-Deferred-Revenue-Balance-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      actions.clearExported();
    }
  }, [exported, actions, filters.end_date]);

  const fetchData = useCallback(
    async (pageNumber, size, rtSorted) => {
      setLoading(true);
      const newSorted = rtSorted || sorted;
      let query = {
        show_negative_balance: !!filters.show_negative_balance,
        ...newSorted.reduce((acc, curr) => {
          acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
          return acc;
        }, {})
      };
      if (filters.contract_status && filters.contract_status.length) {
        query.contract_status = filters.contract_status.map(
          ({ value }) => value
        );
      }
      if (filters.call_status && filters.call_status.length) {
        query.call_status = filters.call_status.map(({ value }) => value);
      }
      if (filters.end_date) {
        query.end_date = filters.end_date;
      }
      if (filters.pageSize || size) {
        query.pageSize = size || filters.pageSize;
      }
      if (typeof filters.page === "number" || pageNumber) {
        query.page = pageNumber || pageNumber === 0 ? pageNumber : filters.page;
      }
      query = queryString.stringify(query, { arrayFormat: "bracket" });
      const { data: result, pages: numPages } = await ReportService.get(
        `/prepaid-deferred-revenue-balance?${query}`
      );

      result.push({
        contract: "PREPAID Total",
        hoursBalance: result.reduce((acc, curr) => acc + curr.hoursBalance, 0),
        amountBalance: result.reduce((acc, curr) => acc + curr.amountBalance, 0)
      });

      setData(result);
      setPages(numPages);
      setLoading(false);
    },
    [filters, setData, setLoading, sorted]
  );

  const handleChange = (name) => (value) => {
    setFilters({ ...filters, [name]: value });
    setLoading(false);
  };

  const handleRowClick = (rowInfo) => {
    history.push(`/app/contracts/${rowInfo.original._id}`);
  };

  const handleSortedChange = async (rtSorted) => {
    setSorted(rtSorted);
    fetchData(null, null, rtSorted);
  };

  const handlePageSizeChange = async (pageSize) => {
    setFilters({ ...filters, pageSize });
    fetchData(null, pageSize);
  };

  const handlePageChange = async (page) => {
    setFilters({ ...filters, page });
    fetchData(page, null);
  };

  const handleExport = async () => {
    setExportingCSV(true);
    const newSorted = sorted;
    let query = {
      show_negative_balance: !!filters.show_negative_balance,
      ...newSorted.reduce((acc, curr) => {
        acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
        return acc;
      }, {})
    };

    if (filters.contract_status && filters.contract_status.length) {
      query.contract_status = filters.contract_status.map(({ value }) => value);
    }
    if (filters.call_status && filters.call_status.length) {
      query.call_status = filters.call_status.map(({ value }) => value);
    }
    if (filters.end_date) {
      query.end_date = filters.end_date;
    }
    query.pageSize = null;
    query.page = null;
    query = queryString.stringify(query, { arrayFormat: "bracket" });

    const { data: result } = await ReportService.get(
      `/prepaid-deferred-revenue-balance?${query}`
    );
    result
      .sort((a, b) => {
        const nameA = a.contract.toUpperCase();
        const nameB = b.contract.toUpperCase();

        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      })
      .push({
        contract: "PREPAID Total",
        hoursBalance: result.reduce((acc, curr) => acc + curr.hoursBalance, 0),
        amountBalance: result.reduce((acc, curr) => acc + curr.amountBalance, 0)
      });

    const title = `Prepaid Deferred Revenue Balance Summary As Of ${moment(
      filters.end_date
    ).format("MM/DD/YYYY")}`;

    downloadCSV(
      `PrepaidDeferredRevenueBalance-${formatDate(moment(), "yyyyMMdd")}`,
      result,
      PREPAID_DEFERRED_REVENUE_BALANCE_COLUMNS,
      title
    );
    setExportingCSV(false);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 0,
      pageSize: 10,
      end_date: formatDate(new Date(), "yyyy-MM-dd")
    });
    setData([]);
  };

  const handleExportPDF = () => {
    const newSorted = sorted;
    const query = {
      show_negative_balance: !!filters.show_negative_balance,
      ...newSorted.reduce((acc, curr) => {
        acc[`sort.${curr.id}`] = curr.desc ? 1 : -1;
        return acc;
      }, {})
    };

    if (filters.contract_status && filters.contract_status.length) {
      query.contract_status = filters.contract_status.map(({ value }) => value);
    }
    if (filters.call_status && filters.call_status.length) {
      query.call_status = filters.call_status.map(({ value }) => value);
    }
    if (filters.end_date) {
      query.end_date = filters.end_date;
    }
    query.pageSize = null;
    query.page = null;
    const payload = {
      data: query,
      type: "PREPAID_DEFERRED_REVENUE_BALANCE",
      format: "pdf"
    };
    actions.performExport(payload);
  };

  return (
    <div className="report-page">
      <PrepaidDeferredRevenueBalanceFilters
        filters={filters}
        onChange={handleChange}
        fetchData={() => fetchData()}
        loading={loading}
        handleResetFilters={handleResetFilters}
        onExportClick={handleExport}
        onPDFExport={handleExportPDF}
        exportDisabled={!data?.length || loading || exportingCSV || exporting}
      />
      <PrepaidDeferredRevenueBalanceList
        data={data}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSortedChange={handleSortedChange}
        page={filters.page}
        pages={pages}
        sorted={sorted}
        pageSize={filters.pageSize}
        handleExport={handleExport}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

PrepaidDeferredRevenueBalancePage.propTypes = {
  history: PropTypes.object,
  actions: PropTypes.object,
  exported: PropTypes.string,
  exporting: PropTypes.bool
};

function mapStatesToProps(state) {
  return {
    exported: state.reducers.exported,
    exporting: state.reducers.exporting
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...exportActions
      },
      dispatch
    )
  };
}

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(PrepaidDeferredRevenueBalancePage);
